/**
 * Chapter013.js — Toolbox / Pre-start Talks (OHSE 013)
 * Migrated from SWMS Platform Chapter013.tsx → JavaScript
 *
 * Props:
 *   data       {array}   toolbox_talks from swmsData
 *   onChange   {fn}      (newTalks) => void
 *   companyName {string} from companyInfo.name
 */

import { useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const inputCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';
const textareaCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all resize-y min-h-[72px]';

function newTalk() {
  return {
    id: crypto.randomUUID(),
    workplace: '',
    subject: '',
    presented_by: '',
    duration: '',
    date: '',
    attendees: [
      { id: crypto.randomUUID(), name: '', signature: '' },
      { id: crypto.randomUUID(), name: '', signature: '' },
    ],
    points_raised: '',
    corrective_actions: [
      { id: crypto.randomUUID(), action: '', action_by: '', sign_off: '', date: '' },
    ],
  };
}

export default function Chapter013({ data = [], onChange, companyName }) {
  const cn = companyName || '[Company Name]';

  const addTalk = useCallback(() => {
    onChange([...data, newTalk()]);
  }, [data, onChange]);

  const updateTalk = useCallback((idx, updated) => {
    const next = [...data];
    next[idx] = updated;
    onChange(next);
  }, [data, onChange]);

  const deleteTalk = useCallback((idx) => {
    onChange(data.filter((_, i) => i !== idx));
  }, [data, onChange]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 013</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">
          Toolbox / Pre-start Talks
        </h2>
      </div>

      {/* Declaration */}
      <div className="p-4 bg-[#f8f7f4] rounded-lg border border-[#e0dbd0] text-sm text-[#1a1a2e] leading-relaxed space-y-2">
        <p>
          All Toolbox / Pre-start Talks undertaken on behalf of{' '}
          <span className="font-semibold text-[#c9a84c]">{cn}</span>{' '}
          are recorded on this form and signed by participants.
        </p>
        <p>
          All corrective actions noted on this form are implemented and signed by the nominated
          person. It is the responsibility of the Works Supervisor to ensure that all corrective
          actions are completed and reviewed for effectiveness.
        </p>
      </div>

      {/* Talk records */}
      {data.map((talk, idx) => (
        <TalkCard
          key={talk.id || idx}
          talk={talk}
          index={idx}
          onChange={(updated) => updateTalk(idx, updated)}
          onDelete={() => deleteTalk(idx)}
        />
      ))}

      {/* Add button */}
      <div className="flex justify-center">
        <button type="button" onClick={addTalk}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] border border-[#c9a84c]/40 transition-colors">
          <Plus className="w-4 h-4" /> Add Toolbox Talk
        </button>
      </div>
    </div>
  );
}

// ── Talk Card ──────────────────────────────────────────────────────────────────

function TalkCard({ talk, index, onChange, onDelete }) {
  const update = (field, value) => onChange({ ...talk, [field]: value });

  // Attendee helpers
  const addAttendee = () => onChange({
    ...talk,
    attendees: [...talk.attendees, { id: crypto.randomUUID(), name: '', signature: '' }],
  });

  const updateAttendee = (aIdx, field, value) => {
    const next = [...talk.attendees];
    next[aIdx] = { ...next[aIdx], [field]: value };
    onChange({ ...talk, attendees: next });
  };

  const removeAttendee = (aIdx) => onChange({
    ...talk,
    attendees: talk.attendees.filter((_, i) => i !== aIdx),
  });

  // Corrective action helpers
  const addCA = () => onChange({
    ...talk,
    corrective_actions: [...talk.corrective_actions, { id: crypto.randomUUID(), action: '', action_by: '', sign_off: '', date: '' }],
  });

  const updateCA = (caIdx, field, value) => {
    const next = [...talk.corrective_actions];
    next[caIdx] = { ...next[caIdx], [field]: value };
    onChange({ ...talk, corrective_actions: next });
  };

  const removeCA = (caIdx) => onChange({
    ...talk,
    corrective_actions: talk.corrective_actions.filter((_, i) => i !== caIdx),
  });

  return (
    <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
      <div className="px-4 py-2.5 bg-[#f0ede6] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#1a1a2e] text-white text-xs font-semibold shrink-0">
            {index + 1}
          </span>
          <span className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">
            Toolbox / Pre-start Talk
          </span>
        </div>
        <button type="button" onClick={onDelete} className="p-1 text-red-400 hover:text-red-600 transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 space-y-5">
        {/* Header fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Workplace">
            <input type="text" value={talk.workplace || ''} placeholder="e.g. Site location"
              onChange={e => update('workplace', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Subject of Talk">
            <input type="text" value={talk.subject || ''} placeholder="e.g. Safety work"
              onChange={e => update('subject', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Presented by">
            <input type="text" value={talk.presented_by || ''} placeholder="Name of presenter"
              onChange={e => update('presented_by', e.target.value)} className={inputCls} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Duration">
              <input type="text" value={talk.duration || ''} placeholder="e.g. 15 min"
                onChange={e => update('duration', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Date">
              <input type="date" value={talk.date || ''}
                onChange={e => update('date', e.target.value)} className={inputCls} />
            </Field>
          </div>
        </div>

        {/* Persons Present */}
        <div className="border-t border-[#e0dbd0] pt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Persons Present</p>
          <div className="space-y-2">
            {talk.attendees.map((att, aIdx) => (
              <div key={att.id || aIdx} className="flex items-center gap-3 border border-[#e0dbd0] rounded-lg p-3 group">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#1a1a2e]/10 text-[#1a1a2e] text-xs font-semibold shrink-0">
                  {aIdx + 1}
                </span>
                <div className="flex-1">
                  <input type="text" value={att.name || ''} placeholder="Full name"
                    onChange={e => updateAttendee(aIdx, 'name', e.target.value)} className={inputCls} />
                </div>
                <div className="flex-1">
                  <input type="text" value={att.signature || ''} placeholder="Signature (type name)"
                    onChange={e => updateAttendee(aIdx, 'signature', e.target.value)} className={inputCls} />
                </div>
                <button type="button" onClick={() => removeAttendee(aIdx)}
                  className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-3">
            <button type="button" onClick={addAttendee}
              className="text-xs text-[#c9a84c] hover:text-[#c9a84c]/80 font-semibold uppercase tracking-wider flex items-center gap-1 transition-colors">
              <Plus className="w-3.5 h-3.5" /> Add Attendee
            </button>
          </div>
        </div>

        {/* Points Raised */}
        <div className="border-t border-[#e0dbd0] pt-4">
          <Field label="Points Raised / Comments">
            <textarea value={talk.points_raised || ''} placeholder="Record any points raised or comments from the talk..."
              onChange={e => update('points_raised', e.target.value)} className={textareaCls} />
          </Field>
        </div>

        {/* Corrective Actions */}
        <div className="border-t border-[#e0dbd0] pt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Corrective Actions</p>
          <div className="space-y-2">
            {talk.corrective_actions.map((ca, caIdx) => (
              <div key={ca.id || caIdx} className="group relative border border-[#e0dbd0] rounded-lg p-3">
                <div className="grid grid-cols-[1fr_120px_120px_110px] gap-3">
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Corrective Action</label>
                    <input type="text" value={ca.action || ''} placeholder="Describe action..."
                      onChange={e => updateCA(caIdx, 'action', e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Action By</label>
                    <input type="text" value={ca.action_by || ''} placeholder="Name"
                      onChange={e => updateCA(caIdx, 'action_by', e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Sign Off</label>
                    <input type="text" value={ca.sign_off || ''} placeholder="Signature"
                      onChange={e => updateCA(caIdx, 'sign_off', e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Date</label>
                    <input type="date" value={ca.date || ''}
                      onChange={e => updateCA(caIdx, 'date', e.target.value)} className={inputCls} />
                  </div>
                </div>
                <button type="button" onClick={() => removeCA(caIdx)}
                  className="absolute top-2 right-2 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-3">
            <button type="button" onClick={addCA}
              className="text-xs text-[#c9a84c] hover:text-[#c9a84c]/80 font-semibold uppercase tracking-wider flex items-center gap-1 transition-colors">
              <Plus className="w-3.5 h-3.5" /> Add Corrective Action
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
